// Tamil-Katral-Maiyam\src\pages\admin\SchoolProfile.tsx

import React, { useState, useEffect } from 'react';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { useAuth } from '../../contexts/AuthContext';
import {
  getSchoolProfile,
  updateSchoolProfile,
  UpdateSchoolProfilePayload,
  patchSchoolSettings,
  PatchSchoolSettingsPayload,
} from '../../modules/admin/api/school.client';
import { School } from '../../modules/platform/types';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler';
import { sanitizePhone, PHONE_MAX_CHARS } from '../../utils/validation';
import { Pencil } from 'lucide-react';
import { GradeFeatureAllocationSection } from './components/GradeFeatureAllocationSection';

const dash = (v: string | null | undefined) => (v != null && String(v).trim() !== '' ? String(v) : '-');

type FormState = {
  name: string;
  code: string;
  timezone: string;
  address: string;
  phone: string;
  website: string;
  description: string;
  contact_email: string;
  contact_phone: string;
  contact_address: string;
  logo_url: string;
  theme_color: string;
  academic_year_start: string;
  academic_year_end: string;
  academic_year_key: string;
  language_ui_default: string;
};

const formFromSchool = (s: School): FormState => ({
  name: s.name ?? '',
  code: s.code ?? '',
  timezone: s.timezone ?? '',
  address: s.contact?.address ?? s.address ?? '',
  phone: s.phone ?? '',
  website: s.website ?? '',
  description: s.description ?? '',
  contact_email: s.contact?.email ?? '',
  contact_phone: s.contact?.phone ?? '',
  contact_address: s.contact?.address ?? '',
  logo_url: s.branding?.logo_url ?? '',
  theme_color: s.branding?.theme_color ?? '',
  academic_year_start: s.school_settings?.academic_year?.start_date ?? '',
  academic_year_end: s.school_settings?.academic_year?.end_date ?? '',
  academic_year_key: s.school_settings?.academic_year?.key ?? '',
  language_ui_default: s.school_settings?.language?.ui_default ?? '',
});

export const SchoolProfile: React.FC = () => {
  const { user } = useAuth();
  const [school, setSchool] = useState<School | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState<FormState>({
    name: '', code: '', timezone: '', address: '', phone: '', website: '', description: '',
    contact_email: '', contact_phone: '', contact_address: '', logo_url: '', theme_color: '',
    academic_year_start: '', academic_year_end: '', academic_year_key: '', language_ui_default: '',
  });

  const schoolId = user?.memberships?.[0]?.school_id;
  const [logoPreviewOk, setLogoPreviewOk] = useState(true);

  useEffect(() => {
    const loadProfile = async () => {
      if (!schoolId) return;
      try {
        const data = await getSchoolProfile(schoolId);
        setSchool(data);
        setForm(formFromSchool(data));
      } catch (error) {
        showErrorToast(error);
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, [schoolId]);

  useEffect(() => {
    if (school && !isEditing) setForm(formFromSchool(school));
  }, [school, isEditing]);

  const handleCancel = () => {
    if (school) setForm(formFromSchool(school));
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!schoolId) return;
    setIsSaving(true);
    try {
      const profilePayload: UpdateSchoolProfilePayload = {
        name: form.name || undefined,
        code: form.code || null,
        timezone: form.timezone || null,
        address: form.address || form.contact_address || null,
        phone: form.phone || null,
        website: form.website || null,
        description: form.description || null,
        contact_email: form.contact_email || null,
        contact_phone: form.contact_phone || null,
        branding: {
          logo_url: form.logo_url || null,
          theme_color: form.theme_color || null,
        },
      };
      await updateSchoolProfile(schoolId, profilePayload);

      const settingsPayload: PatchSchoolSettingsPayload = {};
      if (form.academic_year_start || form.academic_year_end || form.academic_year_key) {
        settingsPayload.academic_year = {};
        if (form.academic_year_start) settingsPayload.academic_year.start_date = form.academic_year_start;
        if (form.academic_year_end) settingsPayload.academic_year.end_date = form.academic_year_end;
        if (form.academic_year_key) settingsPayload.academic_year.key = form.academic_year_key;
      }
      if (form.language_ui_default) {
        settingsPayload.language = { ui_default: form.language_ui_default };
      }
      if (Object.keys(settingsPayload).length > 0) {
        await patchSchoolSettings(schoolId, settingsPayload);
      }

      showSuccessToast('School profile updated.');
      const data = await getSchoolProfile(schoolId);
      setSchool(data);
      setForm(formFromSchool(data));
      setIsEditing(false);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }

  if (!school) {
    return (
      <Layout>
        <div className="text-center py-8 text-gray-600 dark:text-gray-400">School not found.</div>
      </Layout>
    );
  }

  const onboardingStatus = school.onboarding_state?.step ?? school.onboarding_status ?? '-';
  const address = school.contact?.address ?? school.address ?? '-';
  const startDate = school.school_settings?.academic_year?.start_date;
  const endDate = school.school_settings?.academic_year?.end_date;
  const languageUi = school.school_settings?.language?.ui_default ?? '-';
  const themeColor = school.branding?.theme_color;

  return (
    <Layout>
      <PageHeader
        title={isEditing ? form.name || 'School' : dash(school.name)}
        subtitle="School details"
        actions={
          isEditing ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} isLoading={isSaving}>
                Save
              </Button>
            </div>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)}>
              <Pencil className="w-4 h-4 mr-1" />
              Edit
            </Button>
          )
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* B) Snapshot */}
        <Card className="h-full">
          <CardHeader>
            <CardTitle>Snapshot</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Row label="Status" value={dash(school.status)} />
            <Row label="Onboarding status" value={typeof onboardingStatus === 'string' ? onboardingStatus : dash(String(onboardingStatus))} />
            {isEditing ? (
              <>
                <Input label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                <Input label="Timezone" value={form.timezone} onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))} />
              </>
            ) : (
              <>
                <Row label="Timezone" value={dash(school.timezone)} />
              </>
            )}
          </CardContent>
        </Card>

        {/* C) Details */}
        <Card className="h-full">
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {isEditing ? (
              <>
                <Input label="Address" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} />
                <Input label="Phone" type="tel" inputMode="tel" autoComplete="tel" maxLength={PHONE_MAX_CHARS} value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: sanitizePhone(e.target.value) }))} />
                <Input label="Website" value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} />
                <Textarea label="Description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
              </>
            ) : (
              <>
                <Row label="Address" value={dash(address)} />
                <Row label="Phone" value={dash(school.phone)} />
                <Row label="Website" value={dash(school.website)} />
                <Row label="Description" value={dash(school.description)} />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Grid: Academic Year & Contact, Branding & Language — responsive */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {/* 1) Academic Year & Contact — combined */}
        <Card className="h-full">
          <CardHeader>
            <CardTitle>Academic Year & Contact</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {isEditing ? (
              <>
                <Input label="Start date" type="date" value={form.academic_year_start} onChange={(e) => setForm((f) => ({ ...f, academic_year_start: e.target.value }))} />
                <Input label="End date" type="date" value={form.academic_year_end} onChange={(e) => setForm((f) => ({ ...f, academic_year_end: e.target.value }))} />
                <Input label="Key (e.g. 2025-2026)" value={form.academic_year_key} onChange={(e) => setForm((f) => ({ ...f, academic_year_key: e.target.value }))} placeholder="2025-2026" />
                <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
                  <Input label="Email" value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} />
                  <Input label="Address" value={form.contact_address} onChange={(e) => setForm((f) => ({ ...f, contact_address: e.target.value }))} />
                </div>
              </>
            ) : (
              <>
                <Row label="Start date" value={dash(startDate)} />
                <Row label="End date" value={dash(endDate)} />
                {school.school_settings?.academic_year?.key != null && String(school.school_settings.academic_year.key).trim() !== '' && (
                  <Row label="Key" value={dash(school.school_settings.academic_year.key)} />
                )}
                <Row label="Email" value={dash(school.contact?.email)} />
                <Row label="Address" value={dash(school.contact?.address)} />
              </>
            )}
          </CardContent>
        </Card>

        {/* 2) Branding & Language — combined */}
        <Card className="h-full">
          <CardHeader>
            <CardTitle>Branding & Language</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Branding
              </p>

              {isEditing ? (
                <>
                  {/* Logo URL + Live Preview */}
                  <Input
                    label="Logo URL"
                    value={form.logo_url}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm((f) => ({ ...f, logo_url: v }));
                      setLogoPreviewOk(true); // reset error when user edits
                    }}
                  />

                  {(() => {
                    const logoUrl = (form.logo_url || "").trim();
                    if (!logoUrl) return null;

                    return (
                      <div className="flex items-center gap-3 -mt-1">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Preview:
                        </span>

                        <div className="h-14 w-14 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex items-center justify-center overflow-hidden">
                          {logoPreviewOk ? (
                            <img
                              src={logoUrl}
                              alt="Logo preview"
                              className="h-full w-full object-contain"
                              referrerPolicy="no-referrer"
                              onLoad={() => setLogoPreviewOk(true)}
                              onError={() => setLogoPreviewOk(false)}
                            />
                          ) : (
                            <span className="text-[11px] text-gray-500 dark:text-gray-400 px-1 text-center">
                              Invalid image
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Theme color + picker */}
                  {(() => {
                    const normalizeHex = (v: string) => {
                      const s = (v || "").trim();
                      return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(s) ? s : "#4f46e5";
                    };
                    const hex = normalizeHex(form.theme_color);

                    return (
                      <div className="flex items-end gap-3">
                        <div className="flex-1">
                          <Input
                            label="Theme color"
                            value={form.theme_color}
                            onChange={(e) =>
                              setForm((f) => ({ ...f, theme_color: e.target.value }))
                            }
                            placeholder="#hex"
                          />
                        </div>

                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                            Pick
                          </label>
                          <input
                            type="color"
                            value={hex}
                            onChange={(e) =>
                              setForm((f) => ({ ...f, theme_color: e.target.value }))
                            }
                            className="h-10 w-12 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent p-1 cursor-pointer"
                            title="Pick theme color"
                          />
                        </div>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <>
                  {/* View mode: Logo label next to image */}
                  {(() => {
                    const logoUrl = (school.branding?.logo_url || "").trim();

                    return (
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Logo:
                        </span>

                        {logoUrl ? (
                          <div className="h-14 w-14 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex items-center justify-center overflow-hidden">
                            <img
                              src={logoUrl}
                              alt="School logo"
                              className="h-full w-full object-contain"
                              referrerPolicy="no-referrer"
                              onError={(e) => {
                                (e.currentTarget as HTMLImageElement).style.display = "none";
                              }}
                            />
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                        )}
                      </div>
                    );
                  })()}

                  {/* Theme color (view) */}
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="font-medium text-gray-600 dark:text-gray-400">
                      Theme color:
                    </span>{" "}
                    {themeColor != null && String(themeColor).trim() !== "" ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className="h-4 w-4 shrink-0 rounded border border-gray-300 dark:border-gray-600 inline-block align-middle"
                          style={{ backgroundColor: String(themeColor) }}
                          title={String(themeColor)}
                        />
                      </span>
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400">-</span>
                    )}
                  </p>
                </>
              )}
            </div>
            <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Language</p>
              {isEditing ? (
                <Input label="UI default" value={form.language_ui_default} onChange={(e) => setForm((f) => ({ ...f, language_ui_default: e.target.value }))} placeholder="e.g. ta, en" />
              ) : (
                <Row label="UI default" value={dash(languageUi)} />
              )}
            </div>
          </CardContent>
        </Card>

      </div>

      {schoolId ? (
        <div className="mt-8 w-full max-w-none">
          <GradeFeatureAllocationSection schoolId={schoolId} />
        </div>
      ) : null}
    </Layout>
  );
};

/** Inline display: "Field name: value" on one line */
function Row({ label, value }: { label: string; value: string }) {
  return (
    <p className="text-sm text-gray-700 dark:text-gray-300">
      <span className="font-medium text-gray-600 dark:text-gray-400">{label}:</span>{' '}
      <span className="text-gray-900 dark:text-white break-words">{value}</span>
    </p>
  );
}
